// MARK://dhk8/admin-shell/pages/modules/WaitlistDetailPage
// Ficha "ver-primero" de una entrada de cola de espera (Gustavo 2026-06-23). Header
// con nombre + posición/hora + pills (estado + código) + KPIs (personas, espera est.,
// en cola desde) + Tabs (General, Historial).
//
// ES UNA MÁQUINA DE ESTADOS, NO UN REGISTRO EDITABLE. name/phone/partySize son
// INMUTABLES — no hay formulario de edición. La ficha expone las ACCIONES de ciclo
// de vida (las mismas del POS, vía pos-floor):
//   WAITING/NOTIFIED → Notificar · Sentar · Subir/Bajar prioridad · Sin respuesta · Quitar
//   SEATED           → terminal: muestra seatedAt + mesa/sesión (read-only)
//   LEFT/EXPIRED     → terminal: muestra el estado final + cuándo
// status flow: WAITING → NOTIFIED → SEATED | LEFT | EXPIRED.
//
// "Sentar" abre una sesión de mesa y requiere tableId + serverId (covers opcional),
// así que muestra un modal: mesa (floor.getLayout().tables), mesero (users.listUsers)
// y personas (default = partySize). El backend impone tenant/RLS (no va en input).
//
// Carga por id: NO existe wl.getWaitlistEntry(id), así que traemos
// wl.listWaitlist({includeHistory:true}) y hacemos find(id) (mismo patrón que
// ProductDetailPage usa con las recetas).
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ChevronLeft, Bell, Trash2, Users, Phone, Clock,
  Armchair, ArrowUp, ArrowDown, PhoneOff, MapPin,
} from 'lucide-react'

import * as wl from '../../lib/clients/waitlist.js'
import * as floor from '../../lib/clients/floor.js'
import * as users from '../../lib/clients/users.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'
import { useConfirm } from '../../components/ConfirmDialog.js'

type Tab = 'general' | 'historial'

const STATUS_TONE: Record<wl.WaitlistStatus, StatusTone> = {
  WAITING: 'info',
  NOTIFIED: 'warn',
  SEATED: 'ok',
  LEFT: 'neutral',
  EXPIRED: 'neutral',
}
function statusLabel(s: wl.WaitlistStatus): string {
  switch (s) {
    case 'WAITING': return 'Esperando'
    case 'NOTIFIED': return 'Notificada'
    case 'SEATED': return 'Sentada'
    case 'LEFT': return 'Se fue'
    case 'EXPIRED': return 'Expiró'
  }
}
function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
}
function minutesSince(iso: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
}

export default function WaitlistDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const confirm = useConfirm()

  // No hay wl.getWaitlistEntry(id): traemos la cola con historia y hacemos find(id).
  const list = useAsync((s) => wl.listWaitlist({ includeHistory: true }, s), [id])
  const [tab, setTab] = useState<Tab>('general')
  const [busy, setBusy] = useState(false)
  const [seating, setSeating] = useState(false)

  const e = useMemo(() => (list.data ?? []).find((x) => x.id === id) ?? null, [list.data, id])
  const isActive = e?.status === 'WAITING' || e?.status === 'NOTIFIED'

  async function handleNotify() {
    if (!e) return
    setBusy(true)
    try { await wl.notifyWaitlistEntry(e.id); toast('Cliente notificado', 'success'); list.reload() }
    catch (err) { toast(err instanceof Error ? err.message : 'No se pudo notificar.', 'danger') }
    finally { setBusy(false) }
  }
  async function handleMove(direction: wl.MoveDirection) {
    if (!e) return
    setBusy(true)
    try {
      await wl.moveWaitlistEntry(e.id, direction)
      toast(direction === 'UP' ? 'Subió en la fila' : 'Bajó en la fila', 'success')
      list.reload()
    } catch (err) { toast(err instanceof Error ? err.message : 'No se pudo mover.', 'danger') }
    finally { setBusy(false) }
  }
  async function handleNoShow() {
    if (!e) return
    setBusy(true)
    try {
      const updated = await wl.attemptNoShowWaitlistEntry(e.id)
      toast(
        updated.status === 'EXPIRED'
          ? 'Sin respuesta — la entrada expiró'
          : `Intento sin respuesta registrado (${updated.noShowAttempts}/3)`,
        updated.status === 'EXPIRED' ? 'warn' : 'success',
      )
      list.reload()
    } catch (err) { toast(err instanceof Error ? err.message : 'No se pudo registrar.', 'danger') }
    finally { setBusy(false) }
  }
  async function handleRemove() {
    if (!e) return
    const ok = await confirm({
      title: 'Quitar de la fila',
      message: `¿Quitar a ${e.name} de la fila de espera?`,
      confirmLabel: 'Quitar',
      tone: 'danger',
    })
    if (!ok) return
    setBusy(true)
    try { await wl.leaveWaitlistEntry(e.id); toast('Retirado de la fila', 'success'); list.reload() }
    catch (err) { toast(err instanceof Error ? err.message : 'No se pudo retirar.', 'danger') }
    finally { setBusy(false) }
  }

  const kpis = useMemo(() => {
    if (!e) return []
    return [
      { label: 'Personas', value: String(e.partySize) },
      { label: 'Espera est.', value: e.estimatedWaitMin !== null ? `${e.estimatedWaitMin} min` : '—' },
      { label: 'En cola hace', value: `${minutesSince(e.addedAt)} min` },
    ]
  }, [e])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/waitlist')} aria-label="Volver a la cola de espera"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Entrada de fila</h1><p className="adm-page__sub">Cola de espera · ficha de la entrada</p></div>
        </div>
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && !e && (
        <div className="adm-empty">No se encontró esta entrada de fila. Puede haber salido de la cola.</div>
      )}

      {e && (
        <EntityDetailPage
          title={e.name}
          subtitle={`Posición ${e.position} · ${fmtTime(e.addedAt)}`}
          pills={<>
            <StatusPill tone={STATUS_TONE[e.status]} label={statusLabel(e.status)} />
            {e.queueCode && <StatusPill tone="neutral" label={e.queueCode} />}
          </>}
          kpis={kpis.length > 0 ? <SummaryStrip items={kpis} /> : undefined}
          actions={isActive ? (
            <>
              <button className="adm-btn adm-btn--sm" disabled={busy} onClick={() => void handleNotify()} title="Marcar notificada (mesa lista)"><Bell size={14} /> Notificar</button>
              <button className="adm-btn adm-btn--primary adm-btn--sm" disabled={busy} onClick={() => setSeating(true)} title="Sentar (abre una sesión de mesa)"><Armchair size={15} /> Sentar</button>
              <button className="adm-btn adm-btn--sm" disabled={busy || e.position <= 1} onClick={() => void handleMove('UP')} title="Subir prioridad"><ArrowUp size={14} /></button>
              <button className="adm-btn adm-btn--sm" disabled={busy} onClick={() => void handleMove('DOWN')} title="Bajar prioridad"><ArrowDown size={14} /></button>
              <button className="adm-btn adm-btn--sm" disabled={busy} onClick={() => void handleNoShow()} title="Sin respuesta (3 intentos → expira)"><PhoneOff size={14} /> Sin respuesta</button>
              <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={busy} onClick={() => void handleRemove()} title="Quitar de fila"><Trash2 size={15} /></button>
            </>
          ) : undefined}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones de la entrada de fila"
          />}
        >
          {tab === 'general' && (
            <>
              {/* Terminal banners (read-only): la entrada ya no acepta acciones. */}
              {e.status === 'SEATED' && (
                <div className="adm-banner" role="status" style={{ marginBottom: 14, maxWidth: 560 }}>
                  <Armchair size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Sentada el {fmtTime(e.seatedAt)}{e.seatedSessionId ? ` · sesión ${e.seatedSessionId}` : ''}.
                </div>
              )}
              {(e.status === 'LEFT' || e.status === 'EXPIRED') && (
                <div className="adm-banner" role="status" style={{ marginBottom: 14, maxWidth: 560 }}>
                  {e.status === 'LEFT' ? 'El cliente se retiró de la fila.' : `La entrada expiró tras ${e.noShowAttempts} intento(s) sin respuesta.`}
                  {' '}({fmtTime(e.notifiedAt ?? e.addedAt)})
                </div>
              )}

              <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
                <Field label="Cliente">{e.name}</Field>
                <Field label="Código"><span className="adm-mono">{e.queueCode || '—'}</span></Field>
                <Field label="Posición">{e.position}</Field>
                <Field label="Personas"><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Users size={14} /> {e.partySize}</span></Field>
                <Field label="Teléfono">{e.phone ? <a href={`tel:${e.phone}`} style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Phone size={13} /> {e.phone}</a> : <span className="adm-muted">—</span>}</Field>
                <Field label="En cola desde"><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={13} /> {fmtTime(e.addedAt)} · {minutesSince(e.addedAt)} min</span></Field>
                <Field label="Espera estimada">{e.estimatedWaitMin !== null ? `${e.estimatedWaitMin} min` : <span className="adm-muted">—</span>}</Field>
                <Field label="Notificada">{fmtTime(e.notifiedAt)}</Field>
                <Field label="Sentada">{fmtTime(e.seatedAt)}</Field>
                <Field label="Intentos sin respuesta">{e.noShowAttempts > 0 ? `${e.noShowAttempts} / 3` : <span className="adm-muted">—</span>}</Field>
                <Field label="Notas">{e.notes || <span className="adm-muted">—</span>}</Field>
                <Field label="Estado"><StatusPill tone={STATUS_TONE[e.status]} label={statusLabel(e.status)} /></Field>
              </div>
            </>
          )}

          {tab === 'historial' && <AuditTrail entityId={e.id} />}
        </EntityDetailPage>
      )}

      {seating && e && (
        <SeatDialog
          entry={e}
          onClose={() => setSeating(false)}
          onSeated={() => { setSeating(false); toast(`${e.name} sentada`, 'success'); list.reload() }}
        />
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}

// ── Modal "Sentar" ──────────────────────────────────────────────────────────
// seatWaitlistEntry abre una sesión de mesa y exige tableId + serverId (covers
// opcional). Mesas vienen del plano del tenant (floor.getLayout) y meseros del
// staff (users.listUsers, rol mesero/gerente). El backend impone tenant/RLS.
// Exportado para que WaitlistPage (la lista) reuse el MISMO modal en su acción
// "Sentar" por fila — un solo origen de verdad para el flujo de sentar.
export function SeatDialog({
  entry, onClose, onSeated,
}: {
  entry: wl.WaitlistEntryDto
  onClose: () => void
  onSeated: () => void
}) {
  const layout = useAsync((s) => floor.getLayout(s), [])
  const staff = useAsync((s) => users.listUsers(undefined, s), [])

  const [tableId, setTableId] = useState('')
  const [serverId, setServerId] = useState('')
  const [covers, setCovers] = useState<number>(entry.partySize)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const tables = useMemo(
    () => [...(layout.data?.tables ?? [])].sort((a, b) => a.code.localeCompare(b.code, 'es', { numeric: true })),
    [layout.data],
  )
  // Meseros primero, pero permitimos cualquier staff activo (gerente puede sentar).
  const servers = useMemo(() => {
    const order: Record<string, number> = { waiter: 0, manager: 1, owner: 2, cashier: 3, kitchen: 4 }
    return [...(staff.data ?? [])]
      .filter((u) => u.status === 'ACTIVE')
      .sort((a, b) => (order[a.role] ?? 9) - (order[b.role] ?? 9) || a.fullName.localeCompare(b.fullName, 'es'))
  }, [staff.data])

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault()
    if (!tableId) { setErr('Selecciona una mesa'); return }
    if (!serverId) { setErr('Selecciona un mesero'); return }
    setBusy(true); setErr(null)
    try {
      const input: wl.SeatWaitlistInput = { tableId, serverId }
      if (covers > 0) input.covers = covers
      await wl.seatWaitlistEntry(entry.id, input)
      onSeated()
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'No se pudo sentar al cliente')
    } finally {
      setBusy(false)
    }
  }

  const loadErr = layout.error || staff.error
  const loading = layout.loading || staff.loading

  return (
    <div className="adm-modal-bg" role="dialog" aria-modal>
      <form className="adm-modal" onSubmit={(e) => void handleSubmit(e)} style={{ maxWidth: 440 }}>
        <h3 style={{ margin: 0 }}>Sentar a {entry.name}</h3>
        <p className="adm-muted" style={{ marginTop: 0, fontSize: 12 }}>
          Abre una sesión de mesa para {entry.partySize} persona(s).
        </p>

        {loadErr && <div className="adm-banner adm-banner--danger" role="alert">No se pudo cargar mesas o personal.</div>}

        <label className="adm-field">
          <span><MapPin size={12} style={{ verticalAlign: 'middle' }} /> Mesa</span>
          <select className="adm-select" value={tableId} onChange={(e) => setTableId(e.target.value)} disabled={loading || busy} required>
            <option value="">{loading ? 'Cargando…' : 'Selecciona una mesa'}</option>
            {tables.map((t) => (
              <option key={t.id} value={t.id}>{t.code}{t.capacity ? ` · ${t.capacity} lugares` : ''}</option>
            ))}
          </select>
        </label>

        <label className="adm-field">
          <span><Users size={12} style={{ verticalAlign: 'middle' }} /> Mesero</span>
          <select className="adm-select" value={serverId} onChange={(e) => setServerId(e.target.value)} disabled={loading || busy} required>
            <option value="">{loading ? 'Cargando…' : 'Selecciona un mesero'}</option>
            {servers.map((u) => (
              <option key={u.id} value={u.id}>{u.fullName}{u.role ? ` · ${users.STAFF_ROLE_LABEL[u.role as users.StaffRole] ?? u.role}` : ''}</option>
            ))}
          </select>
        </label>

        <label className="adm-field">
          <span>Personas (covers)</span>
          <input
            type="number" min={1} max={50} className="adm-input"
            value={covers}
            onChange={(e) => setCovers(Math.max(1, Number(e.target.value) || 1))}
            disabled={busy}
          />
        </label>

        {!loading && tables.length === 0 && (
          <div className="adm-banner" role="status">No hay mesas en el plano. Crea mesas en el editor del plano primero.</div>
        )}
        {err && <div className="adm-banner adm-banner--danger" role="alert">{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="adm-btn" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className="adm-btn adm-btn--primary" disabled={busy || loading}>
            {busy ? 'Sentando…' : 'Sentar'}
          </button>
        </div>
      </form>
    </div>
  )
}
